import {
  calculateAnalyticalDesign,
  calculatePitchMismatch,
  calculateSensitivities,
  estimateThickParaxial,
  evaluateCandidate,
  predictedImageDistance,
} from '../physics'
import type { AppState, CandidateLens } from '../types'
import { HorizontalScrollRegion } from './HorizontalScrollRegion'
import { Metric, ModelBadge, Notice, Section, StatusBadge, formatNumber } from './ui'

function lensAsCandidate(state: AppState): CandidateLens {
  return state.lens.mode === 'thin'
    ? {
        id: 'active-thin',
        name: 'Active thin-lens specification',
        manufacturer: '',
        mode: 'thin',
        pitchMm: state.lens.thin.pitchMm,
        lpi: state.lens.thin.lpi,
        focalLengthMm: state.lens.thin.focalLengthMm,
        focalReference: state.lens.thin.focalReference,
        radiusMm: null,
        refractiveIndex: null,
        centerThicknessMm: null,
        fillFactor: state.lens.thin.fillFactor,
        pitchToleranceMm: state.lens.thin.pitchToleranceMm,
        focalToleranceMm: state.lens.thin.focalToleranceMm,
        radiusToleranceMm: null,
        thicknessToleranceMm: null,
        lateralOffsetMm: state.lens.thin.lateralOffsetMm,
        notes: '',
      }
    : {
        id: 'active-thick',
        name: 'Active thick-lens specification',
        manufacturer: '',
        mode: 'thick',
        pitchMm: state.lens.thick.pitchMm,
        lpi: null,
        focalLengthMm: null,
        focalReference: 'vendor-unspecified',
        radiusMm: state.lens.thick.radiusMm,
        refractiveIndex: state.lens.thick.refractiveIndex,
        centerThicknessMm: state.lens.thick.centerThicknessMm,
        fillFactor: state.lens.thick.fillFactor,
        pitchToleranceMm: state.lens.thick.pitchToleranceMm,
        focalToleranceMm: null,
        radiusToleranceMm: state.lens.thick.radiusToleranceMm,
        thicknessToleranceMm: state.lens.thick.thicknessToleranceMm,
        lateralOffsetMm: state.lens.thick.lateralOffsetMm,
        notes: '',
      }
}

export function RequiredLensTab({ state }: { state: AppState }) {
  const design = calculateAnalyticalDesign(state)
  const sensitivities = calculateSensitivities(state)
  const actualPitch =
    state.lens.mode === 'thin'
      ? state.lens.thin.useDesignTarget
        ? design.requiredPitchMm
        : state.lens.thin.pitchMm
      : state.lens.thick.pitchMm
  const mismatch = calculatePitchMismatch(
    actualPitch,
    design.requiredPitchMm,
    state.lcd.activeWidthMm,
    design.source.sourcePitchMm,
  )
  const activeCandidate = lensAsCandidate(state)
  const evaluation = state.lens.thin.useDesignTarget && state.lens.mode === 'thin'
    ? null
    : evaluateCandidate(activeCandidate, state)
  const actualFocal = state.lens.thin.useDesignTarget
    ? design.requiredFocalLengthMm
    : state.lens.thin.focalLengthMm
  const imageDistance =
    !state.lens.thin.useDesignTarget && state.lens.thin.focalReference === 'vendor-unspecified'
      ? {
          state: 'insufficient' as const,
          distanceMm: null,
          message: 'Vendor focal reference is unspecified; the value is not used for prediction.',
        }
      : predictedImageDistance(actualFocal, design.gap.reducedGapMm)
  const thickCharacterization =
    state.lens.mode === 'thick'
      ? estimateThickParaxial(
          state.lens.thick.pitchMm,
          state.lens.thick.radiusMm,
          state.lens.thick.refractiveIndex,
          state.lens.thick.centerThicknessMm,
          state.lens.thick.externalIndex,
        )
      : null

  return (
    <div className="tab-stack">
      <div className="result-header">
        <div>
          <ModelBadge>{design.model}</ModelBadge>
          <h1>Required lens</h1>
          <p>Finite-conjugate design target from the entered source lattice, reduced optical gap, and viewing plane.</p>
        </div>
        {design.missing.length ? (
          <StatusBadge status="insufficient" />
        ) : state.lens.mode === 'thin' && state.lens.thin.useDesignTarget ? (
          <StatusBadge status="design-target" />
        ) : evaluation ? (
          <StatusBadge status={evaluation.analyticalStatus} label={evaluation.analyticalLabel} />
        ) : null}
      </div>

      {design.missing.length ? (
        <Notice tone="warning" title="Required inputs are missing">
          {design.missing.join(', ')}. No replacement values have been assumed.
        </Notice>
      ) : null}

      <Section title="Finite-conjugate target" description="All distances are millimeters; the thin-lens viewing distance is measured from the lens plane.">
        <div className="metric-grid primary-results">
          <Metric label="Source pitch q" value={formatNumber(design.source.sourcePitchMm, 7)} unit="mm" />
          <Metric label="Repeat width G = Vq" value={formatNumber(design.source.repeatWidthMm, 7)} unit="mm" />
          <Metric label="Reduced gap d = Σt/n" value={formatNumber(design.gap.reducedGapMm, 7)} unit="mm" />
          <Metric label="Required focal length" value={formatNumber(design.requiredFocalLengthMm, 7)} unit="mm" />
          <Metric label="Required lens pitch" value={formatNumber(design.requiredPitchMm, 7)} unit="mm" />
          <Metric label="Required LPI" value={formatNumber(design.requiredLpi, 5)} unit="LPI" />
          <Metric label="Adjacent zone spacing" value={formatNumber(design.adjacentViewSpacingMm, 4)} unit="mm" />
          <Metric label="Distance for entered spacing" value={formatNumber(design.targetDistanceForSpacingMm, 3)} unit="mm" />
        </div>
      </Section>

      <div className="split-sections">
        <Section title="Infinite-distance comparison" description="The naive estimate omits the finite source-to-lens gap correction.">
          <table className="key-value-table">
            <tbody>
              <tr><th>Infinite-distance pitch P∞ = G</th><td>{formatNumber(design.infinitePitchMm, 7)} mm</td></tr>
              <tr><th>Infinite-distance LPI</th><td>{formatNumber(design.infiniteLpi, 5)} LPI</td></tr>
              <tr><th>PPI ÷ V (pixel-column mode)</th><td>{formatNumber(design.infiniteLpiFromPpi, 5)}</td></tr>
              <tr><th>Finite pitch correction</th><td>{design.requiredPitchMm && design.infinitePitchMm ? formatNumber((design.requiredPitchMm - design.infinitePitchMm) * 1000, 4) : 'Missing'} µm</td></tr>
            </tbody>
          </table>
        </Section>

        <Section title="Actual focal behavior" description={state.lens.mode === 'thin' ? 'Thin-lens finite-image relation.' : 'Numerical near-axis trace through the circular thick lens.'}>
          {state.lens.mode === 'thin' ? (
            <>
              <ModelBadge>analytical first-order estimate</ModelBadge>
              <table className="key-value-table compact-top">
                <tbody>
                  <tr><th>Entered / active focal value</th><td>{formatNumber(actualFocal, 7)} mm</td></tr>
                  <tr><th>Regime</th><td>{imageDistance.state}</td></tr>
                  <tr><th>Predicted real image distance</th><td>{imageDistance.distanceMm ? `${formatNumber(imageDistance.distanceMm, 3)} mm` : 'Not finite'}</td></tr>
                </tbody>
              </table>
              <p className="result-note">{imageDistance.message}</p>
              {!state.lens.thin.useDesignTarget && state.lens.thin.focalReference === 'vendor-unspecified' ? <Notice tone="warning">The numerical relation is displayed for inspection, but candidate status remains insufficient until the vendor reference convention is resolved.</Notice> : null}
            </>
          ) : (
            <>
              <ModelBadge>numerical paraxial characterization of thick geometry</ModelBadge>
              <table className="key-value-table compact-top">
                <tbody>
                  <tr><th>Effective focal behavior</th><td>{formatNumber(thickCharacterization?.effectiveFocalLengthMm, 7)} mm</td></tr>
                  <tr><th>Back focal distance from front vertex</th><td>{formatNumber(thickCharacterization?.paraxialBackFocalDistanceMm, 7)} mm</td></tr>
                  <tr><th>Principal plane from rear flat plane</th><td>{formatNumber(thickCharacterization?.principalPlaneFromRearMm, 7)} mm</td></tr>
                </tbody>
              </table>
              <p className="result-note">Estimated from several symmetric near-axis collimated rays. R/(n−1) is not used as the reported thick-lens focal result.</p>
            </>
          )}
        </Section>
      </div>

      <Section title="Actual pitch mismatch" description="Signed, unwrapped accumulation across the active display width. This is a first-order registry drift, not a modulo-period phase.">
        {mismatch ? (
          <div className="metric-grid">
            <Metric label="Pitch error" value={formatNumber(mismatch.pitchErrorUm, 5)} unit="µm" />
            <Metric label="Pitch error" value={formatNumber(mismatch.pitchErrorPercent, 6)} unit="%" />
            <Metric label="LPI error" value={formatNumber(mismatch.lpiError, 6)} unit="LPI" />
            <Metric label="Local mismatch" value={formatNumber(mismatch.mismatchSourceCells, 6)} unit="source cells" />
            <Metric label="Unwrapped accumulated drift" value={formatNumber(mismatch.accumulatedDriftMm, 6)} unit="mm" />
            <Metric label="Accumulated drift / q" value={formatNumber(mismatch.driftSourceCells, 6)} unit="source cells" />
            <Metric label="Accumulated drift / Pactual" value={formatNumber(mismatch.driftLensPeriods, 6)} unit="lens periods" />
          </div>
        ) : (
          <Notice tone="warning">An actual pitch, required pitch, active width, and source pitch are needed.</Notice>
        )}
      </Section>

      <Section title="Local numerical sensitivities" description="Central finite differences around the entered state. Near singular points are reported as missing rather than expanded into extreme values.">
        <HorizontalScrollRegion className="table-scroll" label="Local numerical sensitivities">
          <table className="data-table">
            <thead><tr><th>Response / parameter</th><th>Derivative</th><th>Units</th><th>Model</th></tr></thead>
            <tbody>
              <tr><td>Viewing distance / focal length</td><td>{formatNumber(sensitivities.viewingDistancePerFocalLength, 5)}</td><td>mm/mm</td><td>Thin-lens finite image</td></tr>
              <tr><td>View spacing / reduced gap</td><td>{formatNumber(sensitivities.viewSpacingPerReducedGap, 5)}</td><td>mm/mm</td><td>First-order view geometry</td></tr>
              <tr><td>Required pitch / viewing distance</td><td>{formatNumber(sensitivities.requiredPitchPerViewingDistance, 8)}</td><td>mm/mm</td><td>Finite-conjugate pitch</td></tr>
              <tr><td>Accumulated drift / pitch error</td><td>{formatNumber(sensitivities.accumulatedDriftPerPitchError, 5)}</td><td>mm/mm</td><td>Unwrapped first-order drift</td></tr>
            </tbody>
          </table>
        </HorizontalScrollRegion>
      </Section>
    </div>
  )
}
